import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, CenterScreenModal, LoadingSpinner, RowSpacer } from '../atoms';
import {
  useErrorHandlerContext,
  useIncidentDetailContext,
  useIncidentReportDetailModalContext,
  useShowGalleryModalContext,
  useUserLanguage,
} from '@/customHooks';
import { Col, Row } from 'react-grid-system';
import {
  useGetIncidentReportDetailQuery,
  useUpdateIncidentReportMutation,
} from '@/graphql/generated';
import { advancedNormalizeDate } from '@/helpers';
import { ShowGalleryPictureModal } from './ShowGalleryPictureModal';
import { NormalFormTextArea } from '../molecules';
import textsData from '@/textData/IncidentReportDetailTexts.json'

const TEXT_LIMIT = 2000;

const ModalWrapper = styled.div`
  width: 80vw;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  margin: 24px 0 12px;
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: #666;
`;

const ImageMiniCardContainer = styled.div`
  border-radius: ${({ theme }) => theme.border.radius.md};
  box-shadow: 0 5px 5px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  & > img {
    width: 100%;
    height: 100%;
    aspect-ratio: 3/2;
    object-fit: cover;
  }
`;

const NoteCard = styled.div`
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const NoteOwner = styled.div`
  font-size: 0.85rem;
  font-weight: bold;
  color: #555;
  margin-bottom: 0.25rem;
`;

const NoteText = styled.p`
  font-size: 0.95rem;
  color: #333;
  margin: 0 0 0.75rem 0;
`;

const NoteDate = styled.div`
  font-size: 0.8rem;
  color: #999;
  text-align: right;
`;

const SuccessText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.s5};
  font-weight: 500;
`

export const IncidentReportDetailModal = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const { isOpen, closeModal, incidentReportId } = useIncidentReportDetailModalContext();
  const { incident } = useIncidentDetailContext();
  const { open: errorHandle } = useErrorHandlerContext();
  const { openModal: showPictureGallery } = useShowGalleryModalContext();

  const [noteText, setNoteText] = useState('');
  const [pictures, setPictures] = useState<File[]>([]);
  const [availableChars, setAvailableChars] = useState(TEXT_LIMIT);
  const [showSuccessText, setShowSuccessText] = useState(false)

  const [downloadedImages, setDownloadedImages] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);

  const { data, loading, error, refetch } = useGetIncidentReportDetailQuery({
    variables: {
      incidentId: incident?.id ?? '',
      incidentReportId: incidentReportId ?? '',
    },
    skip: !isOpen || !incidentReportId || !incident?.id,
  });

  const [updateIncidentReport, { loading: updateLoading }] = useUpdateIncidentReportMutation();

  const report = data?.getIncidentReport;

  useEffect(() => {
    if (error) errorHandle(error);
  }, [error, errorHandle]);

  useEffect(() => {
    const downloadImages = async () => {
      if (!report?.fileUploads || report.fileUploads.length === 0) return;
      
      setIsGalleryLoading(true);
      try {
        const imagePromises = report.fileUploads.map(async (fileUpload) => {
          const response = await fetch(fileUpload.urlToDownload);
          if (!response.ok) throw new Error(`Error downloading image: ${fileUpload.urlToDownload}`);
          const blob = await response.blob();
          const filename = fileUpload.urlToDownload.substring(fileUpload.urlToDownload.lastIndexOf('/') + 1).split('?')[0];
          return new File([blob], filename, { type: blob.type });
        });
        const files = await Promise.all(imagePromises);
        setDownloadedImages(files);
      } catch (e) {
        errorHandle(e);
      } finally {
        setIsGalleryLoading(false);
      }
    };

    if (isOpen && report) {
      downloadImages();
    }

    return () => {
      setDownloadedImages([]);
      setImageUrls(urls => {
        urls.forEach(url => URL.revokeObjectURL(url));
        return [];
      });
    }
  }, [isOpen, report, errorHandle]);

  useEffect(() => {
    if (downloadedImages.length === 0) return;
    const urls = downloadedImages.map(file => URL.createObjectURL(file));
    setImageUrls(urls);
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [downloadedImages]);

  const onChangeHandle = useCallback((text: string | null) => {
    const newText = text ?? '';
    if (newText.length > TEXT_LIMIT) return;
    setNoteText(newText);
    setAvailableChars(TEXT_LIMIT - newText.length);
  }, []);

  const onSubmitNoteHandle = async () => {
    if ((!noteText && pictures.length === 0) || !incidentReportId) return;

    try {
      await updateIncidentReport({
        variables: {
          incidentReportId: incidentReportId,
          input: {
            note: noteText ? { description: noteText } : undefined,
          },
        },
      });

      refetch();
      setShowSuccessText(true)
      setNoteText('');
      setPictures([]);
    } catch (error) {
      errorHandle(error);
    }
  };

  return (
    <CenterScreenModal isOpen={isOpen} onClose={closeModal}>
      <ModalWrapper>
        {loading && <LoadingSpinner />}
        {!loading && report && (
          <>
            <Row>
              <Col xs={12}>
                <h1>{report.subject}</h1>
              </Col>
              <Col xs={12}>
                <Subtitle>{`${texts.lastUpdated}: ${advancedNormalizeDate(userLanguage, report.updatedAt)}`}</Subtitle>
              </Col>
            </Row>
            <Row>
              <Col>
                <SectionTitle>{texts.picturesSection}</SectionTitle>
                {isGalleryLoading ? <LoadingSpinner /> : (
                  <Row gutterWidth={16}>
                    {imageUrls.length > 0 ? imageUrls.slice(-4).map((url, index) => (
                      <Col key={index} xs={6} md={3} style={{ marginBottom: '1rem' }}>
                        <ImageMiniCardContainer>
                          <img src={url} alt={`thumbnail ${index}`} />
                        </ImageMiniCardContainer>
                      </Col>
                    )) : <Col><p>{texts.noImages}</p></Col>}
                  </Row>
                )}
                {imageUrls.length > 0 && <Button onClick={() => showPictureGallery(downloadedImages)}>{texts.showImagesButton}</Button>}
              </Col>
            </Row>
            <RowSpacer height={3} />
            <Row>
              <Col>
                <SectionTitle>{texts.notesSection}</SectionTitle>
                {report.notes && report.notes.length > 0 ? report.notes.map(note => (
                  <NoteCard key={note.id}>
                    <NoteOwner>
                      {(note.ownerFirstName || note.ownerLastName)
                        ? `${note.ownerFirstName ?? ''} ${note.ownerLastName ?? ''}`.trim()
                        : ''}
                    </NoteOwner>
                    <NoteText>{note.text}</NoteText>
                    <NoteDate>{advancedNormalizeDate(userLanguage, note.createdAt)}</NoteDate>
                  </NoteCard>
                )) : <p>{texts.noNotes}</p>}
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <SectionTitle>{texts.addNote.label}</SectionTitle>
                <NormalFormTextArea
                  value={noteText}
                  placeholder={texts.addNote.placeholder}
                  onChange={onChangeHandle}
                  label=""
                />
              </Col>
              <Col>
                <p style={{ fontSize: '0.8rem', color: '#999' }}>{`${availableChars} disponibles`}</p>
              </Col>
            </Row>
            <RowSpacer height={1} />
            { showSuccessText && (
              <Row justify="center">
                <Col xs="content">
                  <SuccessText>{texts.success}</SuccessText>
                </Col>
              </Row>
              )
            }
            <RowSpacer height={1} />
            <Row justify="center">
              <Col xs="content">
                <Button
                  onClick={closeModal}
                  secondary
                >{texts.closeButton}</Button>
              </Col>
              <Col xs="content">
                <Button
                  isLoading={updateLoading}
                  onClick={onSubmitNoteHandle}
                  disabled={!noteText && pictures.length === 0}
                >{texts.submitButton}</Button>
              </Col>
            </Row>
          </>
        )}
      </ModalWrapper>
      <ShowGalleryPictureModal />
    </CenterScreenModal>
  );
};